import React, { useEffect, useState, useContext } from 'react';
import { Table, Select, Tag, Spin, Card, Statistic, Row, Col, Empty, Pagination, Badge, message, Tooltip } from 'antd';
import { UserOutlined, CheckCircleOutlined, ClockCircleOutlined, HourglassOutlined, CalendarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Context } from '../context';
import { Head } from './Head';
import { formatRelativeTime } from '../utils';

const TASK_TYPE_LABELS = {
    auto_translation: 'Автоперевод',
    translation_proofreading: 'Проверка перевода',
    voicing: 'Озвучка',
    voicing_proofreading: 'Проверка озвучки',
    animations_video: 'Анимация',
    animations_video_proofreading: 'Проверка анимации',
    video: 'Видео',
    video_proofreading: 'Проверка видео',
    full_video: 'Полное видео',
    full_video_proofreading: 'Проверка полного видео',
    ts_playlist: 'Плейлисты',
    loading_in_s3: 'Загрузка в S3'
};

const STATUS_COLORS = {
    done: 'success',
    in_progress: 'processing',
    pending: 'default'
};

const STATUS_LABELS = {
    done: 'Выполнено',
    in_progress: 'В работе',
    pending: 'Ожидает'
};

export function UserTasksPage({ onLogout }) {
    const navigate = useNavigate();
    const { people, fetchPeople, updateTaskStatus } = useContext(Context);

    const [selectedUserId, setSelectedUserId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tasksData, setTasksData] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [statusFilter, setStatusFilter] = useState(null);
    const [taskTypeFilter, setTaskTypeFilter] = useState(null);

    // Загружаем пользователей при монтировании
    useEffect(() => {
        if (!people || people.length === 0) {
            fetchPeople();
        }
    }, [people, fetchPeople]);

    // Загружаем задачи при изменении пользователя или фильтров
    useEffect(() => {
        if (selectedUserId) {
            fetchUserTasks();
        }
    }, [selectedUserId, currentPage, pageSize, statusFilter, taskTypeFilter]);

    const fetchUserTasks = async () => {
        if (!selectedUserId) return;

        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                per_page: pageSize.toString()
            });

            if (statusFilter) {
                params.append('status', statusFilter);
            }
            if (taskTypeFilter) {
                params.append('task_type', taskTypeFilter);
            }

            const response = await fetch(
                `https://translation-manag-server.dev.torsunov.pro/user-tasks/${selectedUserId}?${params.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setTasksData(data);
        } catch (error) {
            console.error('Error fetching user tasks:', error);
            setTasksData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleUserChange = (userId) => {
        setSelectedUserId(userId);
        setCurrentPage(1);
    };

    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        if (size !== pageSize) {
            setPageSize(size);
        }
    };

    // Обработчик изменения статуса задачи
    const handleStatusChange = async (record, newStatus) => {
        try {
            // Формируем объект currentTask для передачи существующего id статуса и временных меток
            const currentTask = {
                id: record.status_id,
                status: record.status,
                assignee: { id: record.assignee_id },
                file: record.file || null,
                assigned_at: record.assigned_at || null,
                completed_at: record.completed_at || null
            };

            await updateTaskStatus(
                record.project_id,
                record.part_id,
                record.language_id,
                record.task_type,
                newStatus,
                record.assignee_id,
                currentTask
            );

            message.success('Статус успешно обновлен');
            // Перезагружаем задачи
            fetchUserTasks();
        } catch (error) {
            console.error('Error updating status:', error);
            message.error('Ошибка при обновлении статуса');
        }
    };

    const columns = [
        {
            title: 'Проект',
            dataIndex: 'project_name',
            key: 'project_name',
            render: (text, record) => (
                <a
                    onClick={() => navigate(`/project/${record.project_id}`)}
                    style={{ cursor: 'pointer', color: '#1890ff' }}
                >
                    {text}
                </a>
            ),
        },
        {
            title: 'Часть',
            dataIndex: 'part_id',
            key: 'part_id',
            width: 80,
            render: (text) => <span>#{text}</span>,
        },
        {
            title: 'Язык',
            dataIndex: 'language_id',
            key: 'language_id',
            width: 80,
            render: (text) => <Tag>{text?.toUpperCase()}</Tag>,
        },
        {
            title: 'Тип задачи',
            dataIndex: 'task_type',
            key: 'task_type',
            width: 180,
            render: (text) => TASK_TYPE_LABELS[text] || text,
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (status, record) => (
                <Select
                    value={status || 'pending'}
                    style={{ width: 130 }}
                    size="small"
                    onChange={(newStatus) => handleStatusChange(record, newStatus)}
                    options={[
                        { value: 'done', label: <Tag color="success">Выполнено</Tag> },
                        { value: 'in_progress', label: <Tag color="processing">В работе</Tag> },
                        { value: 'pending', label: <Tag color="default">Ожидает</Tag> },
                    ]}
                />
            ),
        },
        {
            title: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <CalendarOutlined style={{ color: '#8c8c8c' }} />
                    Назначено
                </span>
            ),
            dataIndex: 'assigned_at',
            key: 'assigned_at',
            width: 150,
            render: (assigned_at) => {
                if (!assigned_at) return <span style={{ color: '#bfbfbf' }}>—</span>;
                const relativeTime = formatRelativeTime(assigned_at);
                const fullDate = new Date(assigned_at).toLocaleString('ru-RU', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
                return (
                    <Tooltip title={fullDate} placement="top">
                        <span style={{
                            color: '#595959',
                            cursor: 'help',
                            borderBottom: '1px dotted #d9d9d9'
                        }}>
                            {relativeTime}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    Завершено
                </span>
            ),
            dataIndex: 'completed_at',
            key: 'completed_at',
            width: 150,
            render: (completed_at, record) => {
                if (!completed_at || record.status !== 'done') {
                    return <span style={{ color: '#bfbfbf' }}>—</span>;
                }
                const relativeTime = formatRelativeTime(completed_at);
                const fullDate = new Date(completed_at).toLocaleString('ru-RU', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
                return (
                    <Tooltip title={fullDate} placement="top">
                        <span style={{
                            color: '#52c41a',
                            cursor: 'help',
                            borderBottom: '1px dotted #b7eb8f'
                        }}>
                            {relativeTime}
                        </span>
                    </Tooltip>
                );
            },
        },
    ];

    return (
        <>
            <Head onLogout={onLogout} activeTab="user-tasks" />

            <div style={{ padding: '24px', background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
                <Card style={{ marginBottom: 24 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <UserOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                        <div style={{ flex: 1 }}>
                            <div style={{ marginBottom: 8, fontWeight: 500 }}>Выберите пользователя</div>
                            <Select
                                showSearch
                                placeholder="Поиск по email или имени..."
                                style={{ width: '100%', maxWidth: 400 }}
                                value={selectedUserId}
                                onChange={handleUserChange}
                                filterOption={(input, option) =>
                                    option.label.toLowerCase().includes(input.toLowerCase())
                                }
                                options={people.map((user) => ({
                                    value: user.id,
                                    label: user.name || user.email?.split('@')[0] || 'Unknown',
                                }))}
                                allowClear
                                onClear={() => {
                                    setSelectedUserId(null);
                                    setTasksData(null);
                                }}
                            />
                        </div>

                        {selectedUserId && (
                            <>
                                <div>
                                    <div style={{ marginBottom: 8, fontWeight: 500 }}>Статус</div>
                                    <Select
                                        placeholder="Все статусы"
                                        style={{ width: 150 }}
                                        value={statusFilter}
                                        onChange={(value) => {
                                            setStatusFilter(value);
                                            setCurrentPage(1);
                                        }}
                                        allowClear
                                        options={[
                                            { value: 'done', label: 'Выполнено' },
                                            { value: 'in_progress', label: 'В работе' },
                                            { value: 'pending', label: 'Ожидает' },
                                        ]}
                                    />
                                </div>

                                <div>
                                    <div style={{ marginBottom: 8, fontWeight: 500 }}>Тип задачи</div>
                                    <Select
                                        placeholder="Все типы"
                                        style={{ width: 200 }}
                                        value={taskTypeFilter}
                                        onChange={(value) => {
                                            setTaskTypeFilter(value);
                                            setCurrentPage(1);
                                        }}
                                        allowClear
                                        options={Object.entries(TASK_TYPE_LABELS).map(([key, label]) => ({
                                            value: key,
                                            label: label,
                                        }))}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </Card>

                {loading && (
                    <div style={{ textAlign: 'center', padding: 50 }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16, color: '#666' }}>Загрузка задач...</div>
                    </div>
                )}

                {!loading && !selectedUserId && (
                    <Card>
                        <Empty
                            description="Выберите пользователя для просмотра его задач"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    </Card>
                )}

                {!loading && selectedUserId && tasksData && (
                    <>
                        {/* Статистика */}
                        <Row gutter={16} style={{ marginBottom: 24 }}>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="Всего задач"
                                        value={tasksData.total}
                                        prefix={<UserOutlined />}
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="Выполнено"
                                        value={tasksData.status_counts?.done || 0}
                                        valueStyle={{ color: '#52c41a' }}
                                        prefix={<CheckCircleOutlined />}
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="В работе"
                                        value={tasksData.status_counts?.in_progress || 0}
                                        valueStyle={{ color: '#1890ff' }}
                                        prefix={<ClockCircleOutlined />}
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title="Ожидает"
                                        value={tasksData.status_counts?.pending || 0}
                                        valueStyle={{ color: '#8c8c8c' }}
                                        prefix={<HourglassOutlined />}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* Статистика по типам задач - кликабельные фильтры */}
                        {tasksData.task_type_counts && Object.keys(tasksData.task_type_counts).length > 0 && (
                            <Card style={{ marginBottom: 24 }} title="По типам задач (нажмите для фильтрации)">
                                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                    {Object.entries(tasksData.task_type_counts).map(([type, count]) => (
                                        <Badge
                                            key={type}
                                            count={count}
                                            style={{ backgroundColor: taskTypeFilter === type ? '#52c41a' : '#1890ff' }}
                                        >
                                            <Tag
                                                style={{
                                                    padding: '4px 8px',
                                                    cursor: 'pointer',
                                                    border: taskTypeFilter === type ? '2px solid #52c41a' : '1px solid #d9d9d9',
                                                    backgroundColor: taskTypeFilter === type ? '#f6ffed' : undefined
                                                }}
                                                onClick={() => {
                                                    // Если уже выбран этот фильтр - сбрасываем, иначе устанавливаем
                                                    setTaskTypeFilter(taskTypeFilter === type ? null : type);
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                {TASK_TYPE_LABELS[type] || type}
                                            </Tag>
                                        </Badge>
                                    ))}
                                    {taskTypeFilter && (
                                        <Tag
                                            style={{ padding: '4px 8px', cursor: 'pointer' }}
                                            color="error"
                                            onClick={() => {
                                                setTaskTypeFilter(null);
                                                setCurrentPage(1);
                                            }}
                                        >
                                            Сбросить фильтр ✕
                                        </Tag>
                                    )}
                                </div>
                            </Card>
                        )}

                        {/* Таблица задач */}
                        <Card title={`Задачи пользователя: ${tasksData.user?.name || tasksData.user?.email?.split('@')[0] || 'Unknown'}`}>
                            <Table
                                dataSource={tasksData.tasks}
                                columns={columns}
                                rowKey="status_id"
                                pagination={false}
                                size="middle"
                                locale={{
                                    emptyText: 'Нет задач для отображения'
                                }}
                            />

                            {tasksData.total > pageSize && (
                                <div style={{ marginTop: 16, textAlign: 'right' }}>
                                    <Pagination
                                        current={currentPage}
                                        pageSize={pageSize}
                                        total={tasksData.total}
                                        onChange={handlePageChange}
                                        showSizeChanger
                                        showQuickJumper
                                        showTotal={(total) => `Всего ${total} задач`}
                                    />
                                </div>
                            )}
                        </Card>
                    </>
                )}
            </div>
        </>
    );
}
